import { Image } from "@common";
export default `
<h1>Frequently Asked Questions | <a target="_blank" href="https://cis.polite.edu.sg/lib/Common_Identity_System_Administrator_Guide.pdf">User Guide</a></h1>

<h3>What is CIS?</h3>
<p>
CIS, also known as the Common Identity System, built for Continuing Education & Training (CET) learners for a convenient and secured way to access various learning resources provided by six Institutes of Higher Learnings (IHL) institutions in Singapore. The IHLs are Nanyang Polytechnic, Ngee Ann Polytechnic, Republic Polytechnic, Singapore Polytechnic, Temasek Polytechnic, and Institute of Technical Education.
</p>

<h3>What is CIS Student ID?</h3>
<p>
Student ID is a 7-characters (6 digits + 1 character) unique identifier issued to CET students. An example of Student ID is 6843073R. Using a Student ID, a learner can access the learning resources provided by the IHLs.
</p>

<h3>What Wi-Fi network is available to CET Students?</h3>
<p>
CET students can access the POLITE-CET Wi-Fi network in any of the 6 IHLs campuses when they are successfully enrolled in courses offered by the IHLs. Students can access the Wi-Fi network by logging in with their Student ID.
</p>

<h3>What can I do at the CIS Admin Portal?</h3>
<p>
CIS Admin Portal is a website for administrators to view and manage student accounts, view groups created, and view various reports. Based on the admin rights provided, you will be able to see the admin functionalities available for you. Refer to the user guide for the different roles and access matrix.
</p>

<h3>What are the self-service features that are available for CET students in CIS?</h3>
CIS provides features for CET students to perform the below self-service functionalities without having to reach out to the helpdesk.
<ul>
    <li>Retrieve Student ID</li>
    <li>Re-enable disabled account (*account will be disabled by the system after 90 days of inactivity)</li>
    <li>Forget password</li>
    <li>Change password / Add 2 Factor Authentication (2FA)</li>
    <li>Quick links</li>
</ul>
<p>
For CET Students who like to retrieve their Student ID or Re-enable their disabled account, they will need to verify their identity either using Singpass or through the Email OTP sent to the registered email address at Skills Training & Enhancement Portal (STEP). 
</p>

<h3>What should I do when a CET student cannot login to his/her account?</h3>
<p>CET students may not be able to login to his/her account either due to the account being disabled by the system (after 90 days of inactivity) or disabled by an admin.</p>
<p>To address this issue, IHL Helpdesk Engineers can check the status reason in the Student Management module.</p>
<p>If the account reason is stated as By Azure AD, you can advise the CET student to re-enable his/her account using the self-service functionality available. Refer to CIS User Guide in the FAQ section.</p>
<p>If the account reason is stated as By CIS Admin, before re-enabling the CET student account, please check and follow any applicable Standard Operating Procedures (SOPs) provided by your individual IHL.</p>

<h3>The CET Student is having a problem and couldn’t re-enable his account through the self-service functionality.</h3>

<p>
For account that have been manually disabled by admin, the CET students will not be able to re-enable the account through the self-service functionality available.
</p>
<p>
When the account is disabled by admin, the Status Reason will be shown as ‘By CIS Admin’ in the Student Management module.</br></br>
<img src=${Image.admin_faq_pic0} alt="img" width="400px"/>
</p>
<p>
Please check and follow any applicable Standard Operating Procedures (SOPs) provided by your individual IHL before re-enabling the account.
</p>
<p>To re-enable the student’s account, follow the below steps:</p>
<ol>
    <li>Go to Student Management.</li>
    <li>
    Search for the student using the Quick Search available at the top right of the screen.<br/>
    <img src=${Image.admin_faq_pic1} alt="img" width="800px"/>
    </li>
    <li>Check the Account Status for the Student.</li>
    <li>
    Select the CET student using the checkbox at the left of his/her Student ID.
    <br/>
    <img src=${Image.admin_faq_pic2} alt="img" width="800px"/>
    </li>
    <li>
    Click on Edit.
    <br/>
    <img src=${Image.admin_faq_pic3} alt="img" width="300px"/>
    </li>
    <li>
    Check the Enable checkbox under Account Status and click Save.
    <br/>
    <img src=${Image.admin_faq_pic4} alt="img" width="250px"/>
    </li>
</ol>
<br/>

<h3>What should I do when a CET student need help in resetting his password?</h3>
<p>IHL Helpdesk Engineers can help to trigger the password reset email to the CET student’s registered email address in the Lifelong Learning Portal.</p>
<p>You can also advise the CET student to re-reset his/her password using the self-service functionality available. Refer to CIS User Guide in the FAQ section.</p>
<ol>
    <li>Go to Student Management.</li>
    <li>
    Search for the student using the Quick Search available at the top right of the screen.
    <br/>
    <img src=${Image.admin_faq_pic1} alt="img" width="800px"/>
    </li>
    <li>Select the CET student using the checkbox at the left of his/her Student ID.</li>
    <li>
    Click on Reset Password and an email will be sent to the CET student.
    <br/>
    <img src=${Image.admin_faq_pic6} alt="img" width="300px"/>
    </li>
</ol>
<br/>

<h3>What should I do when a CET student need help in resetting his 2FA?</h3>
<p>IHL Engineers can help to trigger the 2FA reset email to the CET student’s registered email address in the Lifelong Learning Portal.</p>
<p>You can also advise the CET student to reset his/her 2FA configuration using the self-service functionality available. Refer to CIS User Guide in the FAQ section.</p>
<ol>
    <li>Go to Student Management.</li>
    <li>
    Search for the student using the Quick Search available at the top right of the screen.
    </li>
    <li>Select the CET student using the checkbox at the left of his/her Student ID.</li>
    <li>
    Click on Reset 2FA and an email will be sent to the CET student.
    <br/>
    <img src=${Image.admin_faq_pic7} alt="img" width="300px"/>
    </li>
</ol>
<br/>

<h3>Why can’t I see the Reporting module?</h3>
<p>You can only see the Reporting module if you are provided with the Report Admin role. Please reach out to your individual IHL reps so that a service request can be submitted to the CIS AvePoint support team to grant you the appropriate access.</p>

<h3>How can I check the number of logins for an application?</h3>
<p>
You can use the Application Usage or Sign-In Logs report to check the number of logins made for a particular application. 
</p>
<p>The Application Usage report shows the overview of the number of successful and failed sign-in, and unique users for CIS and for each application.</p>
<p>The Sign-In report would provide the detail sign-in activities made by each user for each of the application integrated in CIS. </p>
`;